import type {
  WebsiteAnalyticsData,
  WebsiteAnalyticsSeriesPoint,
} from "@/api/website-analytics-api";

/**
 * Pure helpers for the website analytics widget.
 *
 * Two rules drive most of this file:
 *
 * 1. Month arithmetic and formatting are string operations, never Date ones.
 *    `new Date("2026-07")` parses as UTC midnight, so `toLocaleString` renders
 *    it as June for anyone west of UTC - every US user would see the axis and
 *    tooltips shifted by a month.
 *
 * 2. Absence is never rendered as zero. The provider omits months it has no
 *    estimate for (a real sample was missing 9 of 29 months), and it returns
 *    null for entire domains. "We don't know" and "there was no traffic" are
 *    very different claims to put in front of an investor.
 */

/** Below this, month-on-month movement is noise rather than signal. */
export const FLAT_THRESHOLD_PCT = 0.5;

const MONTH_ABBR = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

const MONTH_FULL = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const MONTH_RE = /^\d{4}-(0[1-9]|1[0-2])$/;

/* -------------------------------------------------------------------------- */
/* Series                                                                      */
/* -------------------------------------------------------------------------- */

/** Drop unusable points, de-duplicate months (last wins), sort ascending. */
export function normalizeSeries(
  raw: WebsiteAnalyticsSeriesPoint[] | null | undefined
): WebsiteAnalyticsSeriesPoint[] {
  if (!Array.isArray(raw)) return [];

  const byMonth = new Map<string, WebsiteAnalyticsSeriesPoint>();
  for (const point of raw) {
    if (!point || typeof point.month !== "string") continue;
    if (!MONTH_RE.test(point.month)) continue;
    if (typeof point.visits !== "number" || !Number.isFinite(point.visits)) continue;
    byMonth.set(point.month, { month: point.month, visits: point.visits });
  }

  // Zero-padded ISO months sort chronologically as plain strings.
  return Array.from(byMonth.values()).sort((a, b) =>
    a.month < b.month ? -1 : a.month > b.month ? 1 : 0
  );
}

/** Step "YYYY-MM" forward one month. String arithmetic, no Date. */
export function nextMonth(month: string): string {
  const [y, m] = month.split("-").map(Number);
  return m === 12 ? `${y + 1}-01` : `${y}-${String(m + 1).padStart(2, "0")}`;
}

export interface ChartPoint {
  month: string;
  /** null marks a month the provider never published. */
  visits: number | null;
  hasData: boolean;
}

/**
 * Expand a sparse series to one row per calendar month.
 *
 * The API omits missing months entirely, so `connectNulls={false}` alone is not
 * enough - recharts would place March next to December and draw a straight line
 * across the hole, which is both a false shape and a false time axis.
 */
export function fillMonthGaps(
  series: WebsiteAnalyticsSeriesPoint[] | null | undefined
): ChartPoint[] {
  const normalized = normalizeSeries(series);
  if (normalized.length === 0) return [];
  if (normalized.length === 1) {
    return [{ month: normalized[0].month, visits: normalized[0].visits, hasData: true }];
  }

  const byMonth = new Map(normalized.map((p) => [p.month, p.visits]));
  const last = normalized[normalized.length - 1].month;
  const out: ChartPoint[] = [];

  let cursor = normalized[0].month;
  for (let guard = 0; guard < 1200; guard++) {
    const visits = byMonth.has(cursor) ? (byMonth.get(cursor) as number) : null;
    out.push({ month: cursor, visits, hasData: visits !== null });
    if (cursor === last) break;
    cursor = nextMonth(cursor);
  }

  return out;
}

/** How many months in the rendered span the provider never published. */
export function countMissingMonths(points: ChartPoint[]): number {
  return points.filter((p) => !p.hasData).length;
}

/* -------------------------------------------------------------------------- */
/* Trend                                                                       */
/* -------------------------------------------------------------------------- */

export type TrendDirection = "up" | "down" | "flat";
export type TrendReason =
  | "ok"
  | "single-point"
  | "no-current"
  | "zero-baseline";

export interface TrendResult {
  /** null means render no arrow at all. */
  direction: TrendDirection | null;
  /** null means render no percentage. */
  changePct: number | null;
  current: number | null;
  currentMonth: string | null;
  previous: number | null;
  previousMonth: string | null;
  /** false when the comparison spans a gap, so copy can name the month. */
  previousIsAdjacent: boolean;
  reason: TrendReason;
}

const NO_TREND: TrendResult = {
  direction: null,
  changePct: null,
  current: null,
  currentMonth: null,
  previous: null,
  previousMonth: null,
  previousIsAdjacent: true,
  reason: "no-current",
};

/**
 * Derive the headline number and its movement from the series.
 *
 * "Current" is the newest month that actually has data, not the current
 * calendar month - the provider lags, often by one or two months, so labelling
 * a stale figure "this month" would be a false claim.
 *
 * "Previous" skips gap months rather than requiring strict calendar adjacency.
 * With roughly a third of months missing in real data, demanding adjacency
 * would suppress the arrow most of the time; instead we compare to the nearest
 * earlier month with data and let the caller say so.
 */
export function deriveTrend(
  data: Pick<WebsiteAnalyticsData, "monthly_series" | "metrics"> | null | undefined
): TrendResult {
  const series = normalizeSeries(data?.monthly_series);

  if (series.length === 0) {
    // Fall back to the headline metric when there is no series at all.
    const headline = data?.metrics?.total_website_visits_monthly ?? null;
    if (headline === null) return NO_TREND;
    return {
      ...NO_TREND,
      direction: null,
      current: headline,
      reason: "single-point",
    };
  }

  const latest = series[series.length - 1];

  if (series.length === 1) {
    return {
      direction: null,
      changePct: null,
      current: latest.visits,
      currentMonth: latest.month,
      previous: null,
      previousMonth: null,
      previousIsAdjacent: true,
      reason: "single-point",
    };
  }

  const prior = series[series.length - 2];
  const previousIsAdjacent = nextMonth(prior.month) === latest.month;

  // Never divide by zero into Infinity - show the arrow, drop the percentage.
  if (prior.visits === 0) {
    return {
      direction: latest.visits > 0 ? "up" : "flat",
      changePct: null,
      current: latest.visits,
      currentMonth: latest.month,
      previous: 0,
      previousMonth: prior.month,
      previousIsAdjacent,
      reason: "zero-baseline",
    };
  }

  const rawPct = ((latest.visits - prior.visits) / prior.visits) * 100;
  const changePct = Math.round(rawPct * 10) / 10;

  const direction: TrendDirection =
    Math.abs(changePct) < FLAT_THRESHOLD_PCT ? "flat" : changePct > 0 ? "up" : "down";

  return {
    direction,
    changePct,
    current: latest.visits,
    currentMonth: latest.month,
    previous: prior.visits,
    previousMonth: prior.month,
    previousIsAdjacent,
    reason: "ok",
  };
}

/* -------------------------------------------------------------------------- */
/* View resolution                                                             */
/* -------------------------------------------------------------------------- */

export type WebsiteAnalyticsView =
  | { kind: "loading" }
  | { kind: "disabled" }
  /** Not premium yet: enabling must activate the deal first. */
  | { kind: "needs_premium" }
  | { kind: "no_website" }
  | { kind: "fetching" }
  | { kind: "no_data"; lastFetchedAt: string | null }
  | { kind: "error"; message: string | null }
  | {
      kind: "ok";
      data: WebsiteAnalyticsData;
      trend: TrendResult;
      points: ChartPoint[];
    };

/**
 * Collapse the query state and the API's `state` into one thing to render.
 *
 * Doing this in a pure function keeps the component a switch statement and
 * makes every branch testable without rendering anything.
 */
export function resolveWebsiteAnalyticsView(input: {
  data: WebsiteAnalyticsData | undefined;
  isLoading: boolean;
  isError: boolean;
  errorMessage?: string | null;
}): WebsiteAnalyticsView {
  const { data, isLoading, isError, errorMessage } = input;

  // A hard failure wins over any stale cached payload.
  if (isError) return { kind: "error", message: errorMessage ?? null };
  if (isLoading && !data) return { kind: "loading" };
  if (!data) return { kind: "loading" };

  if (data.state === "no_website") return { kind: "no_website" };
  if (data.state === "disabled") {
    // Offering "Enable" on a deal with no website is a dead end.
    if (!data.domain) return { kind: "no_website" };
    // Enabling a non-premium deal would 409, so ask for the upgrade up front
    // rather than letting the user click into a failure.
    return data.is_premium === false ? { kind: "needs_premium" } : { kind: "disabled" };
  }
  if (data.state === "not_yet_fetched") return { kind: "fetching" };
  if (data.state === "error") {
    return { kind: "error", message: null };
  }

  const points = fillMonthGaps(data.monthly_series);
  const trend = deriveTrend(data);

  // The provider can answer "ok" with every field null. Without this the widget
  // would render an em-dash next to a trend arrow.
  if (data.state === "no_data" || (points.length === 0 && trend.current === null)) {
    return { kind: "no_data", lastFetchedAt: data.last_fetched_at ?? null };
  }

  return { kind: "ok", data, trend, points };
}

/* -------------------------------------------------------------------------- */
/* Formatters                                                                  */
/* -------------------------------------------------------------------------- */

/** "2026-07" -> "Jul", anchoring the year at January so a long axis stays readable. */
export function formatMonthTick(month: string): string {
  if (!MONTH_RE.test(month)) return month;
  const [year, m] = month.split("-");
  const abbr = MONTH_ABBR[Number(m) - 1];
  return m === "01" ? `${abbr} '${year.slice(2)}` : abbr;
}

/** "2026-07" -> "July 2026". */
export function formatMonthLong(month: string): string {
  if (!MONTH_RE.test(month)) return month;
  const [year, m] = month.split("-");
  return `${MONTH_FULL[Number(m) - 1]} ${year}`;
}

/** "2026-07" -> "Jul 2026". */
export function formatMonthShort(month: string): string {
  if (!MONTH_RE.test(month)) return month;
  const [year, m] = month.split("-");
  return `${MONTH_ABBR[Number(m) - 1]} ${year}`;
}

/** Compact visit count. Null renders as an em-dash, never as zero. */
export function formatCompactVisits(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return "—";
  if (Math.abs(value) >= 1_000_000) {
    return `${(value / 1_000_000).toFixed(1).replace(/\.0$/, "")}M`;
  }
  if (Math.abs(value) >= 1_000) {
    return `${Math.round(value / 1_000)}K`;
  }
  return new Intl.NumberFormat("en-US").format(value);
}

/** Full visit count, for tooltips. */
export function formatVisitsFull(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return "—";
  return new Intl.NumberFormat("en-US").format(value);
}

/** Seconds -> "2m 41s". */
export function formatDuration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || !Number.isFinite(seconds)) return "—";
  const total = Math.round(seconds);
  if (total < 60) return `${total}s`;
  return `${Math.floor(total / 60)}m ${total % 60}s`;
}

/** Bounce rate. The provider reports a 0-1 fraction (verified), not a percent. */
export function formatRate(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return "—";
  return `${Math.round(value * 100)}%`;
}

export function formatPagesPerVisit(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return "—";
  return value.toFixed(1);
}

export function formatRank(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return "—";
  return `#${new Intl.NumberFormat("en-US").format(Math.round(value))}`;
}

/** Signed percentage for the trend chip, e.g. "+12.4%". */
export function formatChangePct(value: number | null | undefined): string | null {
  if (value === null || value === undefined || !Number.isFinite(value)) return null;
  const sign = value > 0 ? "+" : value < 0 ? "−" : "";
  return `${sign}${Math.abs(value).toFixed(1)}%`;
}

/** Strip a URL down to a displayable host. */
export function getHost(url: string | null | undefined): string | null {
  if (!url || typeof url !== "string") return null;
  const host = url
    .trim()
    .toLowerCase()
    .replace(/^[a-z][a-z0-9+.-]*:\/\//, "")
    .split("/")[0]
    .replace(/^www\./, "");
  return host.includes(".") ? host : null;
}
